import { hasKey } from '@/utils/resultsHelper';
import { translatedError } from '@/utils/errors';
import { uniqueByUUID } from '@/utils/uuidHelper';
import fields, { globalFieldLengths } from '@/constants/fields';
import validateFilter, {
  cohortValidationError,
  hasJoinsOnDictionary,
  uniqueDictionaryJoins,
} from '../filters/helper';

const validateFilters = (clause, context, errors, query, isRead, requiresFilters = true) => {
  const filters = clause.filters.filter((f) => !f.isNew);
  if (filters.length === 0 && requiresFilters) {
    const title = 'save.cohort.validation.required.filters.title';
    const message = 'save.cohort.validation.required.filters';

    const error = translatedError(context, title, message);
    error.translation.titleArgs = { name: clause.name };
    errors.push(error);
  }

  filters.forEach((filter) => validateFilter(filter, errors, clause, query, isRead));
};

const validateClauseName = (clause, context, errors) => {
  if (hasKey(clause, 'name') && clause.name.length > globalFieldLengths.name) {
    const title = 'save.cohort.validation.clause.name.title';
    const message = 'validation.max.length';

    const error = translatedError(context, title, message, [fields.NAME], { maximum: globalFieldLengths.name });
    error.translation.titleArgs = { name: clause.name };
    errors.push(error);
  }
};

const validateClauseDictionary = ({ dictionary }, context, errors, isRead) => {
  const isValid = dictionary;
  if (!isValid) {
    const error = cohortValidationError(context, 'required.clause.dictionary', isRead, [fields.DICTIONARIES]);
    error.translation.titleArgs = { name: context.queryName };
    errors.push(error);
  }

  return isValid;
};

const validateClause = (clause, errors, query, isRead, requiresFilters = true) => {
  const context = {
    entity: 'clause',
    queryUUID: query.uuid,
    queryName: query.name,
    clauseUUID: clause.uuid,
    dictionaryCode: clause?.dictionary?.code,
  };

  if (validateClauseDictionary(clause, context, errors, isRead)) {
    validateClauseName(clause, context, errors);
    validateFilters(clause, context, errors, query, isRead, requiresFilters);
  }
};

const clausesByDictionary = ({ clauses }, { dictionary }) => (
  clauses.filter((c) => !c.isNew && c.dictionary && c.dictionary.code === dictionary.code)
);

const clausesByFilter = (query, filter, errors, context) => {
  const clauses = clausesByDictionary(query, filter.join);
  if (clauses.length === 0) {
    const title = 'save.cohort.validation.required.clauses.title';
    const message = 'save.cohort.validation.required.clauses';

    const error = translatedError({
      ...context,
      entity: 'filter',
      filterUUID: filter.uuid,
    }, title, message, [fields.JOIN]);

    error.translation.titleArgs = { name: query.name };
    errors.push(error);
  }

  return clauses;
};

const recursiveClauseDependencies = (query, clause, errors, context, clauses = []) => {
  clauses.push(clause);

  const filters = uniqueDictionaryJoins(clause);
  filters.forEach((filter) => {
    const dependentClauses = clausesByFilter(query, filter, errors, context);
    dependentClauses.forEach((dependentClause) => (
      recursiveClauseDependencies(query, dependentClause, errors, context, clauses)));
  });

  return clauses;
};

const checkForCircularReferences = (query, clause, uuids = [], uuid = null) => {
  if (uuids.includes(uuid)) {
    throw new Error(uuid);
  }

  const filters = uniqueDictionaryJoins(clause);
  if (uuid && filters.length > 0) {
    uuids.push(uuid);
  }

  filters.forEach((filter) => {
    const dependentClauses = clausesByDictionary(query, filter.join);
    dependentClauses.forEach((dependentClause) => (
      checkForCircularReferences(query, dependentClause, uuids, filter.uuid)));
  });
};

const clauseDependencies = (query, clause, errors, context) => {
  checkForCircularReferences(query, clause);
  return uniqueByUUID(recursiveClauseDependencies(query, clause, errors, context));
};

export const cohortClauseParents = ({ clauses }, { dictionary }) => (
  clauses.filter((c) => hasJoinsOnDictionary(c, dictionary))
);

const validateClauses = (query, clauses, errors, context, isRead = false, requiresFilters = true) => {
  let filteredClauses = clauses.filter((c) => !c.isNew);

  if (clauses.length === 0 || (filteredClauses.length === 0 && requiresFilters)) {
    const title = 'save.cohort.validation.required.clauses.title';
    const message = 'save.cohort.validation.required.clauses';

    const error = translatedError(context, title, message, [fields.CLAUSE]);
    error.translation.titleArgs = { name: query.name };
    errors.push(error);
  }

  try {
    const dependencies = filteredClauses.map((c) => clauseDependencies(query, c, errors, context)).flat();
    filteredClauses = uniqueByUUID(dependencies);
  } catch (err) {
    const title = 'save.cohort.validation.circular.clauses.title';
    const message = 'save.cohort.validation.circular.clauses';

    const error = translatedError({
      ...context,
      entity: 'filter',
      filterUUID: err.message,
    }, title, message, [fields.JOIN]);

    error.translation.titleArgs = { name: query.name };
    errors.push();
  }

  filteredClauses.forEach((clause) => validateClause(clause, errors, query, isRead, requiresFilters));

  if (filteredClauses.length === 0 && !requiresFilters) {
    filteredClauses.push({
      ...clauses[0],
      filters: [],
    });
  }

  return filteredClauses;
};

export default validateClauses;
